/**
 * Tradução PT-BR e formatação de atributos e eventos do Traccar.
 *
 * Mantém duas tabelas (atributos e eventos) e uma função `formatAttributeValue`
 * que aplica unidades e semântica ao valor cru. Quando uma chave não estiver
 * mapeada, devolve a chave original como label e o valor toString — assim o
 * sistema sempre mostra algo, em vez de quebrar.
 */

export const ATTRIBUTE_LABELS: Record<string, string> = {
  // GPS / rede
  sat: 'Satélites GPS',
  satellites: 'Satélites GPS',
  hdop: 'Precisão HDOP',
  pdop: 'Precisão PDOP',
  vdop: 'Precisão VDOP',
  rssi: 'Sinal celular',
  operator: 'Operadora',
  cell: 'Célula',
  network: 'Rede',
  gsmSignal: 'Sinal GSM',

  // Estado da máquina
  ignition: 'Em operação',
  motion: 'Em movimento',
  blocked: 'Bloqueio',
  charge: 'Carregando',
  charging: 'Carregando',
  alarm: 'Alarme',
  armed: 'Armada',
  status: 'Status',
  event: 'Código do evento',
  archive: 'Arquivado',
  result: 'Resultado',

  // Distância e movimento
  speed: 'Velocidade',
  course: 'Curso',
  odometer: 'Odômetro',
  distance: 'Distância (este envio)',
  totalDistance: 'Distância total',

  // Energia
  battery: 'Tensão da bateria',
  batteryLevel: 'Nível da bateria',
  power: 'Tensão da fonte',

  // Motor / componentes da máquina
  rpm: 'RPM',
  engineLoad: 'Carga do motor',
  engineTemp: 'Temperatura do motor',
  coolantTemp: 'Temperatura do líquido',
  hours: 'Horas trabalhadas',

  // Consumo
  fuel: 'Consumo',
  fuelLevel: 'Nível de consumo',
  fuelConsumption: 'Consumo médio',
  fuelUsed: 'Total consumido',

  // Operador
  driver: 'Operador',
  driverUniqueId: 'ID do operador',

  // Identificação
  vin: 'Número de série',
  protocol: 'Protocolo',
  raw: 'Pacote bruto',
  type: 'Tipo',

  // Fismatek-specific (apelido fica em outro lugar, mas alguns servers usam attr)
  apelido: 'Apelido',
  nickname: 'Apelido',
  image: 'Imagem',
  photo: 'Foto',
  imageUrl: 'URL da imagem',
  photoUrl: 'URL da foto',
  deviceImage: 'Imagem do dispositivo',
};

export const EVENT_LABELS: Record<string, string> = {
  commandResult: 'Resposta do comando',
  deviceOnline: 'Máquina conectada',
  deviceOffline: 'Máquina desconectada',
  deviceUnknown: 'Sem comunicação',
  deviceInactive: 'Máquina inativa',
  deviceCorrupted: 'Dados corrompidos',
  deviceMoving: 'Em movimento',
  deviceStopped: 'Parada',
  deviceOverspeed: 'Velocidade acima do limite',
  deviceFuelDrop: 'Queda no consumo',
  deviceFuelIncrease: 'Aumento no consumo',
  ignitionOn: 'Operação iniciada',
  ignitionOff: 'Operação encerrada',
  geofenceEnter: 'Entrou na geocerca',
  geofenceExit: 'Saiu da geocerca',
  alarm: 'Alarme',
  textMessage: 'Mensagem da máquina',
  driverChanged: 'Operador trocado',
  maintenance: 'Manutenção',
  media: 'Mídia recebida',
  parking: 'Estacionada',
  bluetoothBeaconEnter: 'Entrou em beacon',
  bluetoothBeaconExit: 'Saiu de beacon',
};

/** Tradução do tipo de evento; cai para a string original se não conhecida. */
export function translateEvent(type: string): string {
  return EVENT_LABELS[type] ?? type;
}

/** Tradução da chave de atributo; cai para a própria chave se não conhecida. */
export function translateAttribute(key: string): string {
  return ATTRIBUTE_LABELS[key] ?? key;
}

function metersToKmString(value: number): string {
  const km = value / 1000;
  return `${km.toLocaleString('pt-BR', { maximumFractionDigits: 2 })} km`;
}

function msToHoursString(ms: number): string {
  const totalMin = Math.floor(ms / 60000);
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  return `${h.toLocaleString('pt-BR')}h ${String(m).padStart(2, '0')}min`;
}

/**
 * Formata o valor de um atributo conforme a chave: aplica unidades, converte
 * unidades brutas (m → km, ms → h, nós → km/h) e traduz booleanos com
 * semântica (ignition true → "Ligada"; blocked true → "Bloqueada").
 */
export function formatAttributeValue(key: string, value: unknown): string {
  if (value === null || value === undefined || value === '') return '—';

  // Booleanos com semântica especial
  if (key === 'ignition') {
    if (value === true || value === 'true') return 'Ligada';
    if (value === false || value === 'false') return 'Desligada';
  }
  if (key === 'blocked') {
    if (value === true || value === 'true') return 'Bloqueada';
    if (value === false || value === 'false') return 'Desbloqueada';
  }
  if (key === 'motion') {
    if (value === true || value === 'true') return 'Sim';
    if (value === false || value === 'false') return 'Não';
  }
  if (key === 'charge' || key === 'charging' || key === 'archive' || key === 'armed') {
    if (value === true || value === 'true') return 'Sim';
    if (value === false || value === 'false') return 'Não';
  }

  // Booleano genérico
  if (typeof value === 'boolean') return value ? 'Sim' : 'Não';

  // Números: converter por chave
  const num = typeof value === 'number' ? value : Number(value);
  if (Number.isFinite(num)) {
    if (key === 'odometer' || key === 'distance' || key === 'totalDistance') {
      return metersToKmString(num);
    }
    if (key === 'hours') return msToHoursString(num);
    if (key === 'batteryLevel') return `${Math.round(num)}%`;
    if (key === 'battery' || key === 'power') return `${num.toFixed(2)} V`;
    if (key === 'speed') return `${Math.round(num * 1.852)} km/h`;
    if (key === 'course') return `${Math.round(num)}°`;
    if (key === 'rssi' || key === 'gsmSignal') return `${num} dBm`;
    if (['hdop', 'pdop', 'vdop'].includes(key)) return num.toFixed(2);
    if (key === 'engineTemp' || key === 'coolantTemp') return `${num.toFixed(1)}°C`;
    if (key === 'fuelLevel' || key === 'fuel' || key === 'fuelUsed') return `${num.toFixed(1)} L`;
    if (key === 'fuelConsumption') return `${num.toFixed(2)} L/h`;
    if (key === 'rpm' || key === 'engineLoad') return num.toLocaleString('pt-BR');
  }

  // Objetos (network, cell): JSON compacto
  if (typeof value === 'object') {
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }

  return String(value);
}

/**
 * Agrupa eventos consecutivos do mesmo tipo, devolvendo um item por grupo
 * com `count` e o `serverTime` mais recente. Útil pra compactar listas longas
 * de deviceOnline/deviceOffline em pares.
 */
export interface GroupedEvent<E> {
  type: string;
  count: number;
  first: E;
  last: E;
}

export function groupConsecutiveByType<E extends { type: string; serverTime?: string }>(
  events: E[],
): GroupedEvent<E>[] {
  const groups: GroupedEvent<E>[] = [];
  for (const e of events) {
    const last = groups[groups.length - 1];
    if (last && last.type === e.type) {
      last.count += 1;
      last.last = e;
    } else {
      groups.push({ type: e.type, count: 1, first: e, last: e });
    }
  }
  return groups;
}
